import { acquireLock, releaseLock } from '@/lock';
import { Module } from '../app/module';
import { dbMigrations } from './db';
import { logInfo } from '../telemetry';

export type MigrationScript = {
  version: number;
  description: string;
  handler: () => Promise<string | void>;
};

type MigrationRunOptions = {
  lockMode?: 'acquire' | 'skip';
};

export async function runMigrations(
  migrations: MigrationScript[],
  { lockMode = 'acquire' }: MigrationRunOptions = {}
) {
  if (migrations.length === 0) {
    return;
  }

  if (lockMode === 'acquire') {
    const hasLock = await acquireLock('migrations');

    if (!hasLock) {
      logInfo('Another instance is running migrations. Skipping migration run.', {
        source: 'migrations',
      });
      return;
    }
  }

  try {
    const versions = migrations.map(({ version }) => version);

    const existingVersions = await dbMigrations.fetch({
      version: { $in: versions },
    });
    const existingVersionSet = new Set(existingVersions.map(({ version }) => version));
    const pendingMigrations = migrations.filter(({ version }) => !existingVersionSet.has(version));

    if (pendingMigrations.length === 0) {
      return;
    }

    logInfo(`Running migrations (${pendingMigrations.length})...`, {
      source: 'migrations',
    });
    for (const { version, description, handler } of pendingMigrations) {
      logInfo(`Running migration v${version}: ${description}`, {
        source: 'migrations',
      });
      try {
        const output = await handler();
        const outputStr = (output || '').toString().trim();
        const maxSize = 15 * 1024 * 1024; // 15MB (leaving 1MB buffer for other fields and MongoDB overhead)
        const truncatedOutput =
          outputStr.length > maxSize
            ? outputStr.slice(0, maxSize) + '\n[Output truncated - exceeded size limit]'
            : outputStr;
        await dbMigrations.upsertOne(
          {
            version,
          },
          {
            $set: {
              version,
              status: 'completed',
              description,
              output: truncatedOutput,
              appliedAt: new Date(),
            },
          }
        );
        logInfo(`Migration v${version} complete`, {
          source: 'migrations',
        });
      } catch (e) {
        if (e instanceof Error) {
          await dbMigrations.upsertOne(
            {
              version,
            },
            {
              $set: {
                version,
                status: 'failed',
                description,
                output: e.message || '',
                appliedAt: new Date(),
              },
            }
          );
          logInfo(`Migration v${version} is failed: ${e.message}`, {
            source: 'migrations',
          });
        }
      }
    }
  } finally {
    if (lockMode === 'acquire') {
      await releaseLock('migrations');
    }
  }
}

export function startMigrations(migrations: MigrationScript[]) {
  setTimeout(() => {
    runMigrations(migrations).catch((err) => {
      console.error('Error running migrations:', err);
    });
  }, 0);
}

export default new Module('_system.migration', {
  stores: [dbMigrations],
});
